import { DragDropProvider, DragEndEvent } from '@dnd-kit/react'
import { isSortable, useSortable } from '@dnd-kit/react/sortable'
import { ReorderOutlined } from '@mui/icons-material'
import {
  Box,
  Button,
  Card,
  Dialog,
  DialogActions,
  DialogContent,
  Table,
  TableCell,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material'
import { useMutation, useQuery } from '@tanstack/react-query'
import React, { FunctionComponent, useCallback, useMemo, useState } from 'react'

import { MyDialogTitle } from '../../../../base/components/Dialog/MyDialogTitle.tsx'
import { StyledTableBody, StyledTableContainer } from '../../../../base/components/MuiTable/StyledTable.tsx'
import { ContainerSkeleton } from '../../../../base/components/skeleton/ContainerSkeleton.tsx'
import { useUtils } from '../../../../base/hooks/useUtils.tsx'
import { swalClose, swalLoading } from '../../../../utils/swal'
import { apiTipoArticuloOrdenar } from '../../api/apiTipoArticuloOrdenar'
import { apiTipoArticuloSimpleListado } from '../../api/apiTipoArticuloSimpleListado'
import { TipoArticuloOrdenarInput, TipoArticuloProps } from '../../interfaces'

interface SortableItemProps {
  id: string
  item: TipoArticuloProps
  index: number
  sortableIndex: number
  isMoved?: boolean
  isOriginalGapTop?: boolean
  isOriginalGapBottom?: boolean
}

const SortableItem = ({
  id,
  item,
  index,
  sortableIndex,
  isMoved,
  isOriginalGapTop,
  isOriginalGapBottom,
}: SortableItemProps) => {
  const { ref, handleRef, isDragging } = useSortable({ id, index: sortableIndex })

  // Combinamos refs para que toda la tarjeta actúe tanto como área droppable y como elemento draggable
  const setRefs = useCallback(
    (node: HTMLElement | null) => {
      ref(node)
      handleRef(node)
    },
    [ref, handleRef],
  )

  return (
    <Card
      ref={setRefs}
      sx={{
        minWidth: 200,
        position: 'relative',
        zIndex: isDragging ? 999 : 'auto',
        cursor: isDragging ? 'grabbing' : 'grab',
        opacity: isDragging ? 0.8 : 1,
        userSelect: 'none',
        // Efecto de elevación al arrastrar
        boxShadow: isDragging ? 6 : 1,
        ...(isOriginalGapTop && { borderTop: '2px dashed #ff9800' }),
        ...(isOriginalGapBottom && { borderBottom: '2px dashed #ff9800' }),
      }}
    >
      <StyledTableContainer>
        <Table size="small">
          {/* El prop bgColor de StyledTableBody o el componente equivalente aplica el color */}
          <StyledTableBody bgColor={isMoved ? 'secondary' : 'primary'}>
            <TableRow>
              <TableCell width={40}>
                <Box display="flex" alignItems="center" gap={1}>
                  <Typography
                    variant="subtitle1"
                    color={isMoved ? 'secondary' : 'primary'}
                    sx={{ fontWeight: 'bold' }}
                  >
                    {index}
                  </Typography>
                </Box>
              </TableCell>
              <TableCell sx={{ maxWidth: 150 }}>
                <Tooltip
                  title={`${item.codigo} - ${item.descripcion}`}
                  enterNextDelay={500}
                  enterDelay={800}
                  disableInteractive
                >
                  <Typography variant={'body2'} sx={{ fontWeight: 400 }} noWrap>
                    {item.descripcion}
                  </Typography>
                </Tooltip>
              </TableCell>
            </TableRow>
          </StyledTableBody>
        </Table>
      </StyledTableContainer>
    </Card>
  )
}

// Reposicionar elementos
const arrayMove = <T,>(array: T[], from: number, to: number): T[] => {
  const newArray = [...array]
  newArray.splice(to < 0 ? newArray.length + to : to, 0, newArray.splice(from, 1)[0])
  return newArray
}

interface OwnProps {
  id: string
  keepMounted: boolean
  open: boolean
  onClose: (value?: boolean) => void
}
type Props = OwnProps

/**
 * Reordenación de tipos de articulo
 * @param props
 * @author Gonzalo
 * @constructor
 */
const TipoArticuloOrdenarDialog: FunctionComponent<Props> = (props) => {
  const { onClose, open, ...other } = props
  const { requestConfirm, showError, toast } = useUtils()

  // Trae los datos y despliega el dialogo
  const { data, isLoading } = useQuery({
    queryKey: ['tipo-articulo-simple-listado-ordenar'],
    queryFn: () => apiTipoArticuloSimpleListado(100),
    enabled: open,
  })

  // Ordena los datos
  const sortData = (dataToSort: TipoArticuloProps[]) => {
    return [...dataToSort].sort((a, b) => {
      const orderA = typeof a.orden === 'number' ? a.orden : 9999
      const orderB = typeof b.orden === 'number' ? b.orden : 9999
      return orderA - orderB
    })
  }

  const originalItems = useMemo(() => (data ? sortData(data) : []), [data])
  // Inicializamos, si existe data y esta open asociamos los datos, caso contrario array vacio
  const [items, setItems] = useState<TipoArticuloProps[]>(() => {
    return data && open ? sortData(data) : []
  })
  const [manuallyMovedIds, setManuallyMovedIds] = useState<string[]>([])

  // Rastreamos cambios
  const [prevData, setPrevData] = useState(data)
  const [prevOpen, setPrevOpen] = useState(open)

  // Reaccionamos a actualizaciones post-montaje
  if (data !== prevData || open !== prevOpen) {
    setPrevData(data)
    setPrevOpen(open)

    if (open && data) {
      setItems(sortData(data))
      setManuallyMovedIds([])
    } else if (!open) {
      setItems([])
      setManuallyMovedIds([])
    }
  }

  // Cuando el usuario realiza el ordenamiento
  const handleDragEnd = (event: DragEndEvent) => {
    if (event.canceled) return
    const { source } = event.operation

    // Guard de tipos para certificar que el elemento es un Sortable
    if (!isSortable(source)) return

    const { initialIndex, index } = source.sortable

    // Si no cambió de posición, evitamos renders
    if (initialIndex === index) return

    setManuallyMovedIds((prev) => Array.from(new Set([...prev, source.id as string])))

    setItems((currentItems) => {
      return arrayMove(currentItems, initialIndex, index)
    })
  }

  // Mutación de los nuevos datos
  const { mutate, isPending } = useMutation({
    mutationFn: (data: TipoArticuloOrdenarInput[]) => apiTipoArticuloOrdenar(data),
    onMutate: () => swalLoading(),
    onSuccess: (res) => {
      if (res) {
        toast.success('Orden actualizado exitosamente')
        onClose(true)
      }
    },
    onError: (err) => showError(err),
    onSettled: () => swalClose(),
  })

  /** Evento de guardar cambios */
  const handleSave = async () => {
    const { confirmed } = await requestConfirm({
      steps: [
        'El re-ordenamiento afecta a la posición de las categorias en los sitemas de ventas y restaurantes',
      ],
    })
    if (!confirmed) return
    const input: TipoArticuloOrdenarInput[] = items.map((item, index) => ({
      codigo: item.codigo,
      orden: index,
    }))
    mutate(input)
  }

  // Funcionalidad: Restablecer
  const handleReset = () => {
    if (data) {
      setItems(sortData(data))
      setManuallyMovedIds([])
    }
  }

  return (
    <Dialog maxWidth="lg" open={open} {...other}>
      <MyDialogTitle icon={ReorderOutlined} onClose={() => onClose()}>
        Ordenar tipos de artículo
      </MyDialogTitle>
      <DialogContent dividers>
        <ContainerSkeleton
          isLoading={isLoading}
          type="custom"
          rows={10}
          cols={5}
          inputHeight={50}
          inputWidth={150}
          gap={1}
        >
          <Box sx={{ touchAction: 'none' }}>
            {/* El núcleo del nuevo drag-and-drop provee la física interna automáticamente */}
            <DragDropProvider onDragEnd={handleDragEnd}>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateRows: 'repeat(10, max-content)',
                  gridAutoFlow: 'column',
                  gap: 1,
                  overflowX: 'auto',
                  pb: 1,
                  pt: 1, // Añadido para que la sombra del drag no se corte
                }}
              >
                {(() => {
                  const originalGaps = manuallyMovedIds
                    .map((id) => ({
                      origIdx: originalItems.findIndex((i) => i._id === id),
                      currIdx: items.findIndex((i) => i._id === id),
                    }))
                    .filter((m) => m.origIdx !== -1 && m.currIdx !== -1 && m.origIdx !== m.currIdx)

                  return items.map((item, absoluteIndex) => {
                    const isMoved =
                      manuallyMovedIds.includes(item._id!) && originalItems[absoluteIndex]?._id !== item._id

                    const isOriginalGapTop = originalGaps.some(
                      (m) => m.origIdx === absoluteIndex && m.currIdx > m.origIdx,
                    )
                    const isOriginalGapBottom = originalGaps.some(
                      (m) => m.origIdx === absoluteIndex && m.currIdx < m.origIdx,
                    )

                    const originalIndex = originalItems.findIndex((orig) => orig._id === item._id)
                    const displayIndex = originalIndex >= 0 ? originalIndex + 1 : absoluteIndex + 1

                    // Retornamos el componente como GRID para mejor control
                    return (
                      <SortableItem
                        key={item._id}
                        id={item._id!}
                        item={item}
                        index={displayIndex}
                        sortableIndex={absoluteIndex}
                        isMoved={isMoved}
                        isOriginalGapTop={isOriginalGapTop}
                        isOriginalGapBottom={isOriginalGapBottom}
                      />
                    )
                  })
                })()}
              </Box>
            </DragDropProvider>
          </Box>
        </ContainerSkeleton>
      </DialogContent>

      <DialogActions sx={{ justifyContent: 'center' }}>
        <Button color="error" onClick={() => onClose()} disabled={isPending}>
          Cerrar
        </Button>
        <Button color="warning" onClick={handleReset} disabled={isPending || isLoading}>
          Restablecer
        </Button>
        <Button onClick={handleSave} variant="contained" disabled={isPending || isLoading}>
          Guardar Orden
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default TipoArticuloOrdenarDialog
